import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * Cadillac CT5 — Blackwing (linkId 2384, project 59).
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2384.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape. All configurationIds are carried over unchanged so the
 * ids stay stable across both apps (and match ui-metadata.ts).
 */

// Every animated part of the CT5 lives on one of these five loaded assets. The
// 1.0 metadata also listed `variants_parts`, which CT5 never loads — dropped so
// the engine is never asked to animate an asset that isn't there.
const ALL_IDS = ['exterior-color', 'common_exterior'];

export const metadata: IMetadata = {
  models: {
    CT5: {
      id: 'CT5',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        blackwing: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6249',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6243',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6245',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'common_interior',
              sku: '6250',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6266',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        // CT5 - V_Series: the variant-specific body kit is loaded as an
        // accessory (accVSeriesVariantParts, below) rather than a defaultSku -
        // it's a message fired on the already-loaded exterior-color asset, not
        // a separate asset fetch. common_interior/dome reuse Blackwing's skus
        // as a placeholder pending V_Series-specific ones.
        v_series: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6944',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6930',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6966',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6266',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        // CT5 - Premium Luxury: shares V_Series' exterior color palette and
        // common_exterior asset (same skus), but has its own variant_parts
        // accessory (accPremiumLuxuryVariantParts, below), interior colors,
        // and common_interior/dome placeholders (see the same note on
        // v_series above).
        premium_luxury: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6944',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6930',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6940',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6266',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        // CT5 - Sport: same shared exterior palette/common_exterior as
        // v_series/premium_luxury, own variant_parts accessory
        // (accSportVariantParts, below) and interior colors, same
        // common_interior/dome placeholder as the other two.
        sport: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6944',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6930',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6959',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6266',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: false,
          sku: '6268',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: true,
          sku: '6266',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '#ff1300,#0040FF,#ff9f51,#aafef4,#00b6ff,#61ff87',
        ambientLightings: {
          ambientRed: {
            isDefault: true,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [255, 19, 0],
            emissiveIntensity: 0.99,
          },
          ambientBlue: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [0, 64, 255],
            emissiveIntensity: 0.99,
          },
          ambientOrange: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [255, 159, 81],
            emissiveIntensity: 0.99,
          },
          ambientCyan: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [170, 254, 244],
            emissiveIntensity: 0.99,
          },
          ambientSkyBlue: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [0, 182, 255],
            emissiveIntensity: 0.99,
          },
          ambientGreen: {
            isDefault: false,
            identifier: 'common_interior',
            variantIdentifiers: {
              blackwing: 'common_interior',
              v_series: 'interior-color',
              premium_luxury: 'interior-color',
              sport: 'interior-color',
            },
            materialName: [
              'INT_AMBIENT_GLOW',
              'INT_DOOR_AMBIENT_LIGHT',
              'INT_AMBIENT_02_GLOW',
              'INT_AMBIENT_LIGHT',
            ],
            parentNodeName: [],
            emissiveColor: [97, 255, 135],
            emissiveIntensity: 0.99,
          },
        },
        relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
      },
      colors: {
        interior: {
          '03wk11dwf99s': {
            sku: '6245',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          j50tispotnihpo: {
            sku: '6247',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          j5jjtistnihpo: {
            sku: '6241',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          j90d0tistnihpo: {
            sku: '6270',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          j340d0tistnihpo: {
            sku: '6267',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          // 6th Blackwing interior (GM RPO HEA) - the third Jet Black, per the
          // CT5-V catalogue: HMR / HGM / HEA.
          intBlackwingJetBlackPerformance6269: {
            sku: '6269',
            project: '59',
            relevantVariants: ['blackwing'],
            notCompatibleExteriorColors: [],
          },
          // V_Series interior colors, per the real asset manifest: default is
          // "Jet Black Leather Seat" (sku 6966) - NOT Sedona Sauvage, that's
          // sku 6964 (intVSeriesPlatinum6964), which is locked to
          // pkgVSeriesPlatinum via uimetadata.models.CT5.colorPackageLocks
          // (see useColorPackageLocks).
          intVSeriesJetBlack6966: {
            sku: '6966',
            project: '59',
            relevantVariants: ['v_series'],
            notCompatibleExteriorColors: [],
          },
          intVSeries6955: {
            sku: '6955',
            project: '59',
            relevantVariants: ['v_series'],
            notCompatibleExteriorColors: [],
          },
          intVSeries6965: {
            sku: '6965',
            project: '59',
            relevantVariants: ['v_series'],
            notCompatibleExteriorColors: [],
          },
          intVSeriesPlatinum6964: {
            sku: '6964',
            project: '59',
            relevantVariants: ['v_series'],
            notCompatibleExteriorColors: [],
          },
          // Premium Luxury interior colors. No color is locked to Platinum
          // here (unlike v_series/6964) - none was specified.
          intPremiumLuxuryDefault6940: {
            sku: '6940',
            project: '59',
            relevantVariants: ['premium_luxury'],
            notCompatibleExteriorColors: [],
          },
          intPremiumLuxury6945: {
            sku: '6945',
            project: '59',
            relevantVariants: ['premium_luxury'],
            notCompatibleExteriorColors: [],
          },
          intPremiumLuxury6942: {
            sku: '6942',
            project: '59',
            relevantVariants: ['premium_luxury'],
            notCompatibleExteriorColors: [],
          },
          intPremiumLuxury6958: {
            sku: '6958',
            project: '59',
            relevantVariants: ['premium_luxury'],
            notCompatibleExteriorColors: [],
          },
          // Sport interior colors. intSportPlatinum6968 is locked to
          // pkgSportPlatinum via uimetadata.models.CT5.colorPackageLocks
          // (same mechanism as v_series/6964).
          intSportDefault6959: {
            sku: '6959',
            project: '59',
            relevantVariants: ['sport'],
            notCompatibleExteriorColors: [],
          },
          intSport6938: {
            sku: '6938',
            project: '59',
            relevantVariants: ['sport'],
            notCompatibleExteriorColors: [],
          },
          intSport6961: {
            sku: '6961',
            project: '59',
            relevantVariants: ['sport'],
            notCompatibleExteriorColors: [],
          },
          intSportPlatinum6968: {
            sku: '6968',
            project: '59',
            relevantVariants: ['sport'],
            notCompatibleExteriorColors: [],
          },
        },
        exterior: {
          '30ja6fs1si26': {
            sku: '6243',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          '1d0y9evbmngc': {
            sku: '6240',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          mwsv1fnhg5wz: {
            sku: '6248',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          onwrm9wz90ia: {
            sku: '6246',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          bpsnuu0maoxi: {
            sku: '6242',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          bpffsnuu0maoxi: {
            sku: '6244',
            project: '59',
            relevantVariants: ['blackwing'],
          },
          // V_Series exterior colors: default 6930, plus 7 optional.
          extVSeriesDefault6930: {
            sku: '6930',
            project: '59',
            relevantVariants: ['v_series', 'premium_luxury', 'sport'],
          },
          extVSeries6931: {
            sku: '6931',
            project: '59',
            relevantVariants: ['v_series'],
          },
          extVSeries6937: {
            sku: '6937',
            project: '59',
            relevantVariants: ['v_series', 'sport'],
          },
          extVSeries6946: {
            sku: '6946',
            project: '59',
            relevantVariants: ['v_series', 'premium_luxury', 'sport'],
          },
          extVSeries6939: {
            sku: '6939',
            project: '59',
            relevantVariants: ['v_series', 'premium_luxury', 'sport'],
          },
          extVSeries6943: {
            sku: '6943',
            project: '59',
            relevantVariants: ['v_series', 'premium_luxury', 'sport'],
          },
          extVSeries6952: {
            sku: '6952',
            project: '59',
            relevantVariants: ['premium_luxury'],
          },
          extVSeries6951: {
            sku: '6951',
            project: '59',
            relevantVariants: ['premium_luxury', 'sport'],
          },
        },
      },
      features: {
        '50fyijsa8h88': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zarbo501dlfo: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '1e0q63itjg52': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'SHADER_Carbon_Fiber_lower_spliters',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Carbon_Fiber_lower_spliters',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sdkeibj4zedx: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        wfyhlnfxcntc: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Sound_System',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_TOP',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_BASE',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // 1.0 played this feature with a DOORS_CLOSE toggle baked into its
          // message list; 2.0 models the same thing as an interaction toggle.
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        ja3opivostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '9v7g6wt62yu6': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        g6h9z7ho9usu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        ja3evostgzo4: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesExteriorDesign: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxuryExteriorDesign: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportExteriorDesign: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Exterior_Design',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesFrontGrille: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxuryFrontGrille: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportFrontGrille: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'SHADER_Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Front_grille',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesInfotainmentScreen: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxuryInfotainmentScreen: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportInfotainmentScreen: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'SHADER_Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infotainment_screen',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesSoundSystem: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Sound_System',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_TOP',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_BASE',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // 1.0 played this feature with a DOORS_CLOSE toggle baked into its
          // message list; 2.0 models the same thing as an interaction toggle.
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featPremiumLuxurySoundSystem: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Sound_System',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_TOP',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_BASE',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // 1.0 played this feature with a DOORS_CLOSE toggle baked into its
          // message list; 2.0 models the same thing as an interaction toggle.
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featSportSoundSystem: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Sound_System',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_TOP',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'SHADER_Sound_System_BASE',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          // 1.0 played this feature with a DOORS_CLOSE toggle baked into its
          // message list; 2.0 models the same thing as an interaction toggle.
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featVSeriesSteeringWheel: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxurySteeringWheel: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportSteeringWheel: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Steering_Wheel',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesCharging: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxuryCharging: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportCharging: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featVSeriesAmbientLighting: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featPremiumLuxuryAmbientLighting: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featSportAmbientLighting: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Ambient_Lighting',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ihmz2ch7lru2: {
              add: false,
              snapToLastFrame: true,
              persistOnFeatureChange: true,
            },
          },
        },
        featVSeriesDriverModeSelector: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['v_series'],
          messages: [
            {
              message: 'Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featPremiumLuxuryDriverModeSelector: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['premium_luxury'],
          messages: [
            {
              message: 'Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featSportDriverModeSelector: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['sport'],
          messages: [
            {
              message: 'Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode_selector',
              identifiers: ['common_exterior'],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        // Four features added for Blackwing from the client sheet. Message
        // names are the identifiers it gave; no SHADER_* companions were
        // supplied, so only the base message fires.
        featBlackwingEngine: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Engine',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featBlackwingBrakes: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Brakes',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featBlackwingHUD: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'HUD',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        featBlackwingSteeringWheelVMode: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: ['blackwing'],
          messages: [
            {
              message: 'Steering_Wheel_V_Mode',
              identifiers: ['common_exterior'],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        // --- Variant parts: the variant-specific body kit, loaded as a
        // hidden always-on accessory (not a defaultSku) - it's a message
        // fired on the already-loaded exterior-color asset, so it needs
        // exterior-color's identifier rather than a separate asset fetch.
        accVSeriesVariantParts: {
          // RPO 6953 (V_Series)
          view: 'exterior',
          groupId: 'v_series-variant-parts',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'V_Series', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Sunroof (v_series-sunroof): both members hidden - per spec the
        // sunroof must never appear as a pickable option, for any color. It's
        // only ever applied via pkgVSeriesPlatinum's accessoryIds. WO_Sunroof
        // is a colorbody interaction (message fired on the exterior-color
        // asset), matching premium_luxury/sport's sunroof - kept consistent
        // for W_Sunroof too, its paired on/off state.
        accVSeriesSunroofOff: {
          // RPO 6934 (WO_Sunroof, default)
          view: 'exterior',
          groupId: 'v_series-sunroof',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesSunroofOn: {
          // RPO 6933 (W_Sunroof, UltraView power sunroof - part of Platinum Package)
          view: 'exterior',
          groupId: 'v_series-sunroof',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Seatbelt (v_series-seatbelt)
        accVSeriesSeatbeltDefault: {
          // RPO 6967 (Seatbelt_Default) - SeatBelt group (V_Series only)
          view: 'interior',
          groupId: 'v_series-seatbelt',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Seatbelt_Default', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_SEATBELT', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_SEATBELT', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesSeatbeltRed: {
          // RPO 6957 (Seatbelt_Red, "Torch Red seat belt color")
          view: 'interior',
          groupId: 'v_series-seatbelt',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Seatbelt_Red', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_SEATBELT', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_SEATBELT', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Brakes (v_series-brakes): 3-way, same shape as the 2387 Caliper precedent
        accVSeriesBrakesDefault: {
          // RPO 6948 (Brakes_J56_Default) - Caliper group (V_Series, Sport).
          // NOTE: the Roof/SeatBelt/Caliper/Alloy reference sheet lists this as
          // RPO 6941 (swapped with Sport's Brakes_JL9, listed there as 6948) -
          // flagged for confirmation, kept as originally specified for now.
          view: 'exterior',
          groupId: 'v_series-brakes',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Brakes_J56_Default', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesBrakesBlue: {
          // RPO 6947 (Brakes_J56_Blue, "Blue brake calipers")
          view: 'exterior',
          groupId: 'v_series-brakes',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Brakes_J56_Blue', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesBrakesRed: {
          // RPO 6962 (Brakes_J56_Red, "Red brake calipers")
          view: 'exterior',
          groupId: 'v_series-brakes',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Brakes_J56_Red', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Alloy wheels (v_series-alloy): 57M is the default, hidden like other
        // hidden base-state defaults (sunroof/technology off-states above) - only
        // the QB0 upgrade below shows as a pickable card.
        accVSeriesAlloy57M: {
          // RPO 6956 (Alloy_57M, default)
          view: 'exterior',
          groupId: 'v_series-alloy',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Alloy_57M', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesAlloyQB0: {
          // RPO 6954 (Alloy_QB0)
          view: 'exterior',
          groupId: 'v_series-alloy',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Alloy_QB0', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Technology Package content (v_series-technology): bundled only via
        // pkgVSeriesTechnology.accessoryIds - no standalone accessory card, the
        // package is what shows in the Packages UI.
        accVSeriesTechnologyOff: {
          // RPO 6949 (WO_Technology_Package, default)
          view: 'interior',
          groupId: 'v_series-technology',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Technology_Package', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accVSeriesTechnologyOn: {
          // RPO 6950 (W_Technology_Package)
          view: 'interior',
          groupId: 'v_series-technology',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Technology_Package', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['v_series'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Variant parts: same pattern as accVSeriesVariantParts above.
        accPremiumLuxuryVariantParts: {
          // RPO 6932 (Premium_Luxury)
          view: 'exterior',
          groupId: 'premium_luxury-variant-parts',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Premium_Luxury', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Premium Luxury sunroof (premium_luxury-sunroof). Same messages as
        // v_series' sunroof, but shown for all interior colors (per spec) -
        // accPremiumLuxurySunroofOn is display:true, unlike v_series' hidden one.
        accPremiumLuxurySunroofOff: {
          // RPO 6934 (WO_Sunroof, default) - Roof group (Premium_Luxury, Sport)
          view: 'exterior',
          groupId: 'premium_luxury-sunroof',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPremiumLuxurySunroofOn: {
          // RPO 6933 (W_Sunroof, UltraView power sunroof - shown for all interior colors)
          view: 'exterior',
          groupId: 'premium_luxury-sunroof',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'W_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Brakes (premium_luxury-brakes): single fixed look, no alternate
        // color was specified, so this is just the auto-applied default state.
        accPremiumLuxuryBrakesDefault: {
          // RPO 6941 (Brakes_JL9)
          view: 'exterior',
          groupId: 'premium_luxury-brakes',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Brakes_JL9', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Alloy wheels (premium_luxury-alloy): QE1 is the default, hidden like
        // other hidden base-state defaults (sunroof/technology off-states above) -
        // only the Q84 upgrade below shows as a pickable card.
        accPremiumLuxuryAlloyQE1: {
          // RPO 6963 (Alloy_QE1, default)
          view: 'exterior',
          groupId: 'premium_luxury-alloy',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Alloy_QE1', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPremiumLuxuryAlloyQ84: {
          // RPO 6960 (Alloy_Q84)
          view: 'exterior',
          groupId: 'premium_luxury-alloy',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Alloy_Q84', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Technology Package content (premium_luxury-technology): bundled
        // only via pkgPremiumLuxuryTechnology.accessoryIds.
        accPremiumLuxuryTechnologyOff: {
          // RPO 6971 (WO_Technology_Package_Lux, default)
          view: 'interior',
          groupId: 'premium_luxury-technology',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Technology_Package_Lux', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accPremiumLuxuryTechnologyOn: {
          // RPO 6972 (W_Technology_Package_Lux)
          view: 'interior',
          groupId: 'premium_luxury-technology',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Technology_Package_Lux', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['premium_luxury'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Variant parts: same pattern as accVSeriesVariantParts above.
        // Message is "Sports" per the real asset manifest / original spec
        // table, resolving a "Sport" vs "Sports" discrepancy in favor of
        // the manifest.
        accSportVariantParts: {
          // RPO 6935 (Sports)
          view: 'exterior',
          groupId: 'sport-variant-parts',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Sports', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Sport sunroof (sport-sunroof). Same messages as v_series'/
        // premium_luxury's sunroof; shown for all interior colors like
        // premium_luxury (accSportSunroofOn is display:true).
        accSportSunroofOff: {
          // RPO 6934 (WO_Sunroof, default) - Roof group (Premium_Luxury, Sport)
          view: 'exterior',
          groupId: 'sport-sunroof',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSportSunroofOn: {
          // RPO 6933 (W_Sunroof, UltraView power sunroof - shown for all interior colors)
          view: 'exterior',
          groupId: 'sport-sunroof',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'W_Sunroof', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ROOF', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Brakes (sport-brakes): 3-way, default matches premium_luxury's
        // Brakes_JL9, optional Blue/Red match v_series' brake messages.
        accSportBrakesDefault: {
          // RPO 6941 (Brakes_JL9) - Caliper group (V_Series, Sport).
          // NOTE: the reference sheet lists this as RPO 6948 (swapped with
          // V_Series' Brakes_J56_Default, listed there as 6941) - flagged for
          // confirmation, kept as originally specified for now.
          view: 'exterior',
          groupId: 'sport-brakes',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Brakes_JL9', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSportBrakesBlue: {
          // RPO 6947 (Brakes_J56_Blue, "Blue brake calipers")
          view: 'exterior',
          groupId: 'sport-brakes',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Brakes_J56_Blue', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSportBrakesRed: {
          // RPO 6962 (Brakes_J56_Red, "Red brake calipers")
          view: 'exterior',
          groupId: 'sport-brakes',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Brakes_J56_Red', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_CALIIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Alloy wheels (sport-alloy): Q83 is the default, hidden like other
        // hidden base-state defaults (sunroof/technology off-states above) - only
        // the 57M upgrade below shows as a pickable card.
        accSportAlloyQ83: {
          // RPO 6936 (Alloy_Q83, default)
          view: 'exterior',
          groupId: 'sport-alloy',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Alloy_Q83', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSportAlloy57M: {
          // RPO 6956 (Alloy_57M)
          view: 'exterior',
          groupId: 'sport-alloy',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Alloy_57M', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [
            { message: 'SHADER_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- Technology Package content (sport-technology): bundled only via
        // pkgSportTechnology.accessoryIds. Uses the plain (non-"_Lux") message
        // names, same as v_series.
        accSportTechnologyOff: {
          // RPO 6949 (WO_Technology_Package, default)
          view: 'interior',
          groupId: 'sport-technology',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Technology_Package', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accSportTechnologyOn: {
          // RPO 6950 (W_Technology_Package)
          view: 'interior',
          groupId: 'sport-technology',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Technology_Package', identifiers: ['exterior-color'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['sport'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // --- CT5-V Blackwing optional parts, from the GM mapping sheet.
        // Calipers: one group so J6H (standard) and J57 (carbon ceramic) stay
        // mutually exclusive on the car; the two UI categories are split in
        // ui-metadata's `category`, not here.
        accBlackwingCaliperJ6HDefault: {
          // RPO 7067 (Caliper_J6H_Default)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Caliper_J6H_Default', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCaliperJ6HBlue: {
          // RPO 7075 (Caliper_J6H_Blue)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J6H_Blue', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCaliperJ6HRed: {
          // RPO 7074 (Caliper_J6H_Red)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J6H_Red', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCaliperJ6HBronze: {
          // RPO 7076 (Caliper_J6H_Bronze)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J6H_Bronze', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // Carbon Ceramic Brakes with cross-drilled rotors (J57). The sheet
        // lists no default/off state for this set - deselecting returns to
        // Caliper_J6H_Default via the shared group above.
        accBlackwingCaliperJ57Blue: {
          // RPO 7068 (Caliper_J57_Blue)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J57_Blue', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCaliperJ57Red: {
          // RPO 7066 (Caliper_J57_Red)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J57_Red', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCaliperJ57Bronze: {
          // RPO 7070 (Caliper_J57_Bronze)
          view: 'exterior',
          groupId: 'blackwing-caliper',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Caliper_J57_Bronze', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CALIPER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // Carbon Fiber 1 / 2 Packages: independent WO_/W_ on-off pairs, same
        // hidden-default shape as the technology on/off pairs above.
        accBlackwingCarbonFiber1Off: {
          // RPO 7063 (WO_Carbon_Fiber_1)
          view: 'exterior',
          groupId: 'blackwing-carbonfiber1',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Carbon_Fiber_1', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CARBONFIBER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCarbonFiber1On: {
          // RPO 7073 (W_Carbon_Fiber_1)
          view: 'exterior',
          groupId: 'blackwing-carbonfiber1',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Carbon_Fiber_1', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CARBONFIBER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCarbonFiber2Off: {
          // RPO 7064 (WO_Carbon_Fiber_2)
          view: 'exterior',
          groupId: 'blackwing-carbonfiber2',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'WO_Carbon_Fiber_2', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CARBONFIBER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingCarbonFiber2On: {
          // RPO 7065 (W_Carbon_Fiber_2)
          view: 'exterior',
          groupId: 'blackwing-carbonfiber2',
          display: false,
          isDefault: false,
          applyMessages: [
            { message: 'W_Carbon_Fiber_2', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_CARBONFIBER', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        // Alloys (blackwing-alloy): Q63 is the hidden default, same shape as
        // the 57M/QE1 defaults on the other trims.
        accBlackwingAlloyQ63: {
          // RPO 7071 (Alloy_Q63_19in)
          view: 'exterior',
          groupId: 'blackwing-alloy',
          display: false,
          isDefault: true,
          applyMessages: [
            { message: 'Alloy_Q63_19in', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingAlloyQ61: {
          // RPO 7069 (Alloy_Q61_19in)
          view: 'exterior',
          groupId: 'blackwing-alloy',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Alloy_Q61_19in', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
        accBlackwingAlloyRHL: {
          // RPO 7072 (Alloy_RHL_19in)
          view: 'exterior',
          groupId: 'blackwing-alloy',
          display: true,
          isDefault: false,
          applyMessages: [
            { message: 'Alloy_RHL_19in', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          cameraMessages: [
            { message: 'CAMERA_ALLOY', identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false },
          ],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: ['blackwing'],
          notAvailableForColor: { interior: [], exterior: [] },
        },
      },
      packages: {
        // Own groupId, deliberately not shared with Platinum's - sharing a
        // groupId with a package that's also your dependent's dependency
        // causes a self-eviction bug (see the one real dependencyPackages
        // precedent in 2365/graphics-metadata.ts).
        pkgVSeriesTechnology: {
          groupId: 'v_series-technology-pkg',
          accessoryIds: ['accVSeriesTechnologyOn'],
          dependencyPackages: [],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['v_series'],
        },
        pkgVSeriesPlatinum: {
          groupId: 'v_series-platinum-pkg',
          accessoryIds: ['accVSeriesSunroofOn'],
          dependencyPackages: [{ id: 'pkgVSeriesTechnology', autoRemove: false }],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['v_series'],
        },
        // Premium Luxury: same dependency shape as V_Series' packages (own
        // groupId, not shared with Platinum's) - only difference is content.
        pkgPremiumLuxuryTechnology: {
          groupId: 'premium_luxury-technology-pkg',
          accessoryIds: ['accPremiumLuxuryTechnologyOn'],
          dependencyPackages: [],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['premium_luxury'],
        },
        pkgPremiumLuxuryPlatinum: {
          groupId: 'premium_luxury-platinum-pkg',
          accessoryIds: ['accPremiumLuxurySunroofOn'],
          dependencyPackages: [{ id: 'pkgPremiumLuxuryTechnology', autoRemove: false }],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['premium_luxury'],
        },
        // Carbon Fiber 1 / 2 as real packages so they render with the same
        // PackageCard UI the other trims' packages use. Each bundles its own
        // W_ accessory; the WO_ default stays the hidden off-state.
        pkgBlackwingCarbonFiber1: {
          groupId: 'blackwing-carbonfiber1-pkg',
          accessoryIds: ['accBlackwingCarbonFiber1On'],
          dependencyPackages: [],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['blackwing'],
        },
        pkgBlackwingCarbonFiber2: {
          groupId: 'blackwing-carbonfiber2-pkg',
          accessoryIds: ['accBlackwingCarbonFiber2On'],
          dependencyPackages: [],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['blackwing'],
        },
        pkgSportTechnology: {
          groupId: 'sport-technology-pkg',
          accessoryIds: ['accSportTechnologyOn'],
          dependencyPackages: [],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['sport'],
        },
        pkgSportPlatinum: {
          groupId: 'sport-platinum-pkg',
          accessoryIds: ['accSportSunroofOn'],
          dependencyPackages: [{ id: 'pkgSportTechnology', autoRemove: false }],
          notAvailableForColor: { interior: [], exterior: [] },
          relevantVariants: ['sport'],
        },
      },
      interactions: {
        ihmz2ch7lru2: {
          type: 'basic',
          view: ['exterior', 'interior'],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: ['common_exterior', 'common_interior', 'exterior-color', 'interior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: ['common_exterior', 'common_interior', 'exterior-color', 'interior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
        },
        '53ehrrkf0yxy': {
          type: 'basic',
          view: ['exterior'],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: ['common_exterior', 'common_interior', 'exterior-color', 'interior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: ['common_exterior', 'common_interior', 'exterior-color', 'interior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
        },
        rc1z7dwmbfif: {
          type: 'basic',
          view: ['exterior', 'interior'],
          startMessages: [
            {
              message: 'SUNROOF_OPEN',
              identifiers: ['common_interior', 'common_exterior', 'exterior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'SUNROOF_CLOSE',
              identifiers: ['common_interior', 'common_exterior', 'exterior-color'],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7049,
                beta: 0.115,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 4.7145,
                beta: 3.1212,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 4.7222,
                beta: 1.8799,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
          // Gated on the sunroof-on accessory of whichever variant is active:
          // never shown for Blackwing (which has no sunroof at all - this also
          // fixes a pre-existing gap where the toggle showed for Blackwing
          // despite it having no sunroof); for V_Series only shown once
          // Platinum Package (the only way to add accVSeriesSunroofOn) is
          // active; for Premium Luxury and Sport shown whenever the sunroof
          // accessory itself is on, since it's directly toggleable there.
          availableFor: {
            accessories: ['accVSeriesSunroofOn', 'accPremiumLuxurySunroofOn', 'accSportSunroofOn'],
          },
        },
        w3yjdzjao2i0: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: true,
              
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
        },
        toijdriu1av5: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DRL_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
        },
        g64qit5yzfp2: {
          type: 'basic-light',
          view: ['exterior'],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: ALL_IDS,
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.7054,
                beta: 1.4258,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: ['blackwing', 'v_series', 'premium_luxury', 'sport'],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.4,
            alpha: 2.02,
            beta: 1.47,
            radius: 10,
            upperRadiusLimit: 11,
            lowerRadiusLimit: 8,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: ['common_exterior'],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: ['common_exterior'],
          },
        },
        // CT5 has only two interior seat cameras, but ICameraNames requires a
        // third key. Left inert here and omitted from ui-metadata, so the
        // Viewport's seat picker (which reads the UI hotspots) still lists two.
        interiorCamera_2: {
          type: 'interior',
          messages: {},
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
